import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Printer, ArrowLeft, Mail } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const TOKEN_KEY = "exoplusbio-admin-token";

export default function InvoicePage() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const token = localStorage.getItem(TOKEN_KEY);

  const load = () =>
    fetch(`${API}/api/admin/orders/${id}`, { headers: { Authorization: `Bearer ${token || ""}` } })
      .then(async (r) => {
        const d = await r.json();
        if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Failed to load order");
        setOrder(d);
      })
      .catch((e) => setError(e.message));
  useEffect(() => { load(); }, [id]);

  const emailInvoice = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/admin/orders/${id}/email-invoice`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token || ""}` },
        body: JSON.stringify({}),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Failed to send invoice");
      toast.success(`Invoice emailed to ${d.to}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="mx-auto max-w-[720px] px-5 py-16">
        <p className="text-sm text-[#b3261e]">{error}</p>
        <Link to="/admin" data-testid="invoice-back-link" className="mt-4 inline-block text-xs text-[#0e8c8c] underline-offset-2 hover:underline">← Back to admin</Link>
      </div>
    );
  }
  if (!order) {
    return <div className="mx-auto max-w-[720px] px-5 py-16"><p className="font-lab text-xs uppercase tracking-[0.14em] text-[#56636b]">Loading invoice…</p></div>;
  }

  const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;

  return (
    <div className="mx-auto max-w-[760px] px-5 py-10" data-testid="invoice-page">
      <div className="no-print mb-6 flex flex-wrap gap-3">
        <button onClick={() => window.print()} data-testid="invoice-print-button"
          className="inline-flex items-center gap-2 bg-[#0a0a0a] px-5 py-3 text-xs font-medium text-white transition-colors hover:bg-[#0e8c8c]">
          <Printer className="h-4 w-4" /> Print / Save as PDF
        </button>
        {order.customer_email && (
          <button onClick={emailInvoice} disabled={busy} data-testid="invoice-email-button"
            className="inline-flex items-center gap-2 border border-[#e4e9ec] px-5 py-3 text-xs font-medium transition-colors hover:border-[#0e8c8c] hover:text-[#0e8c8c] disabled:opacity-50">
            <Mail className="h-4 w-4" /> {busy ? "Sending…" : `Email to ${order.customer_email}`}
          </button>
        )}
        <Link to="/admin" data-testid="invoice-back-link"
          className="inline-flex items-center gap-2 border border-[#e4e9ec] px-5 py-3 text-xs font-medium transition-colors hover:border-[#0e8c8c] hover:text-[#0e8c8c]">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
      </div>

      <div className="border border-[#e4e9ec] bg-white p-8 md:p-12" data-testid="invoice-document">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-lg font-bold tracking-[3px] text-[#0a0a0a]">EXOPLUS BIO</p>
            <p className="mt-1 text-[11px] text-[#56636b]">exoplusbio.com · info@exoplusbio.com</p>
          </div>
          <div className="text-right">
            <p className="text-[22px] font-bold tracking-[2px] text-[#0e8c8c]">INVOICE</p>
            <p className="mt-1 text-xs text-[#56636b]">
              INV-{order.id.slice(0, 8).toUpperCase()}<br />
              {fmtDate(order.created_at)}
            </p>
          </div>
        </div>

        <div className="mt-8 border-t border-[#e4e9ec] pt-6">
          <p className="text-[11px] uppercase tracking-[2px] text-[#56636b]">Billed to</p>
          <p className="mt-2 text-sm text-[#0a0a0a]">
            {order.customer_name || "—"}
            {order.customer_email ? <><br />{order.customer_email}</> : null}
            {order.customer_phone ? <><br />{order.customer_phone}</> : null}
          </p>
        </div>

        <table className="mt-8 w-full border-collapse text-sm" data-testid="invoice-items">
          <thead>
            <tr className="bg-[#0e8c8c] text-white">
              <th className="px-3 py-2.5 text-left font-medium">Item</th>
              <th className="px-3 py-2.5 text-center font-medium">Qty</th>
              <th className="px-3 py-2.5 text-right font-medium">Unit</th>
              <th className="px-3 py-2.5 text-right font-medium">Amount</th>
            </tr>
          </thead>
          <tbody>
            {order.items.map((i, ix) => (
              <tr key={ix} className="border-b border-[#e4e9ec]">
                <td className="px-3 py-2.5">{i.name}</td>
                <td className="px-3 py-2.5 text-center">{i.quantity}</td>
                <td className="px-3 py-2.5 text-right">{money(i.price_cents)}</td>
                <td className="px-3 py-2.5 text-right">{money(i.price_cents * i.quantity)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-4 text-right">
          <p className="text-base font-bold text-[#0a0a0a]" data-testid="invoice-total">
            Total due: {money(order.total_cents)} {order.currency}
          </p>
        </div>

        {order.notes && <p className="mt-6 text-sm text-[#333333]"><strong>Notes:</strong> {order.notes}</p>}

        <p className="mt-10 border-t border-[#e4e9ec] pt-4 text-[11px] text-[#888888]">
          Thank you for your business. Exoplus Bio products are cosmetics. Questions about this invoice: info@exoplusbio.com
        </p>
      </div>
    </div>
  );
}

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-CA", { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return iso || "";
  }
}